import React from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import cn from 'classnames';
import InfiniteScroll from 'react-infinite-scroll-component';
import { CheckMarkIcon } from 'components/elements/Icons';
import { dateUtils } from 'utils';
import { routes, periods } from 'const';
import { useFetchPagedAppointments, useAuth } from 'hooks';
import Appointment from './Appointment';
import Styles from './styles';

const useStyles = makeStyles(Styles);

function AppointmentsList({ period }) {
  const classes = useStyles();
  const { t } = useTranslation('labels');
  const history = useHistory();
  const { data: appointments, hasNextPage, fetchNextPage, isLoading, total } = useFetchPagedAppointments({ period });
  const onSelectAppointment = (id) => () => history.push(`${routes.appointments}/${id}`);
  const isPast = period === periods.history;
  const { id: userId } = useAuth();

  return (
    <div className={classes.appointmentsList} data-testid="appointments-list">
      {appointments.length ? (
        <div className={classes.appointmentsWrapper} id="scrollable">
          <InfiniteScroll
            className={classes.scrollWrapper}
            dataLength={total}
            next={fetchNextPage}
            hasMore={hasNextPage}
            loader={<span className={classes.info}>{t('loading')}</span>}
            scrollableTarget="scrollable"
            endMessage={<span className={classes.info}>{t('you_have_seen_all_appointments')}</span>}
          >
            <Timeline classes={{ root: classes.timeline }}>
              {appointments.map(({ id, attributes }) => {
                const {
                  online,
                  startDateTime,
                  endDateTime,
                  roomFullName,
                  roomFullNameWithoutFees,
                  providerName,
                  providerPronoun,
                  healthcareServiceName,
                  participants,
                  statusLabel,
                  isCancellationRequested,
                } = attributes;
                const patientCancellable = attributes['patientCancellable?'];
                const timelineDotClasses = cn(
                  classes.timelineDot,
                  { [classes.todayTimelineDot]: dateUtils.isDateToday(startDateTime) && !isPast },
                  { [classes.pastTimelineDot]: isPast },
                );

                const isConfirmed = participants?.some(({ id: participantId, isConfirmed: participantConfirmed }) => participantId === userId && participantConfirmed);
                return (
                  <TimelineItem classes={{ root: classes.timelineItem }} key={id}>
                    <TimelineSeparator>
                      <TimelineDot classes={{ root: timelineDotClasses }} color="primary" variant={dateUtils.isDateToday(startDateTime) || isPast ? 'filled' : 'outlined'}>
                        {isPast && <CheckMarkIcon classes={classes.checkMarkIcon} />}
                      </TimelineDot>
                      <TimelineConnector classes={{ root: classes.timelineConnector }} />
                    </TimelineSeparator>
                    <TimelineContent classes={{ root: classes.timelineContent }}>
                      <Appointment
                        online={online}
                        appointmentStatus={statusLabel}
                        startDateTime={startDateTime}
                        endDateTime={endDateTime}
                        roomFullName={roomFullName}
                        roomFullNameWithoutFees={roomFullNameWithoutFees}
                        providerName={providerName}
                        providerPronoun={providerPronoun}
                        healthcareServiceName={healthcareServiceName}
                        isConfirmed={isConfirmed}
                        isPast={isPast}
                        id={id}
                        onSelect={onSelectAppointment(id)}
                        patientCancellable={patientCancellable}
                        isCancellationRequested={isCancellationRequested}
                      />
                    </TimelineContent>
                  </TimelineItem>
                );
              })}
            </Timeline>
          </InfiniteScroll>
        </div>
      ) : (
        <div className={classes.loadingInfoCaption}>
          <Typography variant="subtitle1" component="span">
            {isLoading ? t('loading') : t('no_appointments_available')}
          </Typography>
        </div>
      )}
    </div>
  );
}

AppointmentsList.propTypes = {
  period: PropTypes.string.isRequired,
};

export default AppointmentsList;
